import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { usePreferredContrast, usePreferredDark, usePreferredReducedMotion } from '@vueuse/core';
import { useA11yStore } from '@/stores/a11y';

export interface A11yOverrides {
    reducedMotion?: boolean | null;
    highContrast?: boolean | null;
    darkScheme?: boolean | null;
}

type OverrideKey = keyof A11yOverrides;

/**
 * Reactive accessibility preferences. Reads OS-level prefs by default, with
 * an optional override that wins (used by Settings to honor user choice).
 *
 * When `overrides` is omitted, the user-level Settings overrides from the
 * a11y Pinia store are used automatically.
 */
export function useA11yPreferences(overrides?: MaybeRefOrGetter<A11yOverrides | null | undefined>) {
    const osReducedMotion = usePreferredReducedMotion();
    const osContrast = usePreferredContrast();
    const osDark = usePreferredDark();

    const a11yStore = (() => {
        try {
            return useA11yStore();
        } catch {
            return null;
        }
    })();

    function override(key: OverrideKey): boolean | null {
        const value = toValue(overrides)?.[key];
        if (typeof value === 'boolean') {
            return value;
        }
        const fromStore = a11yStore?.overrides[key as 'reducedMotion' | 'highContrast'];

        return typeof fromStore === 'boolean' ? fromStore : null;
    }

    const prefersReducedMotion = computed(() => {
        const o = override('reducedMotion');

        return o === null ? osReducedMotion.value === 'reduce' : o;
    });

    const prefersHighContrast = computed(() => {
        const o = override('highContrast');

        return o === null ? osContrast.value === 'more' : o;
    });

    const prefersDarkScheme = computed(() => {
        const o = override('darkScheme');

        return o === null ? osDark.value : o;
    });

    return {
        prefersReducedMotion,
        prefersHighContrast,
        prefersDarkScheme,
    };
}
